import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import writerIcon from '../../img/icons/prosecaptain-writer.svg';
import chatIcon from '../../img/icons/prosecaptain-assistant.svg';
import { useIsSuperAdmin } from '../../features/analytics/auth/useIsSuperAdmin';

export default function AppSelectorPage({ firstName = '', trialEnded = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isSuperAdmin } = useIsSuperAdmin();
  const appOrigin = window.location.origin;
  const cleanName = String(firstName || '').trim();
  const displayName = cleanName || t('appChooser.fallbackName');
  const writerFallbackUrl = trialEnded ? '/pricing' : `${appOrigin}/writer/dashboard`;

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(writerFallbackUrl);
  };

  const appTiles = useMemo(() => ([
    {
      key: 'agent',
      label: t('appChooser.tiles.agent'),
      description: t('appChooser.tiles.agentDescription'),
      href: trialEnded ? '/pricing' : `${appOrigin}/agent/dashboard`,
      icon: chatIcon
    },
    {
      key: 'writer',
      label: t('appChooser.tiles.writer'),
      description: t('appChooser.tiles.writerDescription'),
      href: trialEnded ? '/pricing' : `${appOrigin}/writer/dashboard`,
      icon: writerIcon
    },
    // Super admin analytics: admins only, reachable with an expired trial.
    ...(isSuperAdmin ? [{
      key: 'analytics',
      label: t('appChooser.tiles.analytics'),
      description: t('appChooser.tiles.analyticsDescription'),
      href: `${appOrigin}/admin`,
      IconComponent: ChartBarIcon
    }] : []),
  ]), [appOrigin, t, trialEnded, isSuperAdmin]);

  return (
    <section className="relative w-full px-6 py-16">
      <button
        type="button"
        onClick={handleBack}
        className="absolute left-6 top-6 z-10 inline-flex h-12 w-12 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
        aria-label="Go back"
        title="Back"
      >
        <ArrowLeftIcon className="h-5 w-5" />
      </button>
      <div className="mx-auto max-w-5xl">
      <div className="mb-10">
        <h1 className="text-3xl font-normal tracking-tight text-gray-900 sm:text-4xl">
          {t('appChooser.greeting', { name: displayName })}
        </h1>
        <p className="mt-3 text-base text-gray-600">
          {t('appChooser.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
        {appTiles.map((tile) => (
          <a
            key={tile.key}
            href={tile.href}
            className="group rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-opacity hover:opacity-90"
          >
            {tile.IconComponent ? (
              <tile.IconComponent className="h-12 w-12 text-primary-500" aria-hidden="true" />
            ) : (
              <img
                src={tile.icon}
                alt={tile.label}
                className="h-12 w-12 object-contain"
              />
            )}
            <div className="mt-4 text-lg font-semibold text-gray-900">{tile.label}</div>
            <p className="mt-1 text-sm text-gray-600">{tile.description}</p>
          </a>
        ))}
      </div>
      </div>
    </section>
  );
}
